import { useNavigate } from 'react-router-dom';
import { FileText, FileSignature, ArrowRight } from 'lucide-react';

const Sales = () => {
    const navigate = useNavigate();

    return (
        <div data-testid="sales-choice-page" className="space-y-10 animate-fadeIn">
            {/* Header */}
            <div className="max-w-3xl">
                <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">
                    Point of Sale
                </p>
                <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1A1A] tracking-tight leading-tight">
                    What would you like to do?
                </h1>
                <p className="text-gray-500 mt-3 text-base">
                    Choose how this transaction should be recorded — a full itemized invoice or a formal quotation offer.
                </p>
            </div>

            {/* Choice Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl">
                {/* Create Invoice Card */}
                <button
                    data-testid="choice-create-invoice"
                    onClick={() => navigate('/sales/invoice')}
                    className="choice-card group text-left bg-white border-2 border-[#E5E5E5] rounded-2xl p-8 lg:p-10 hover:border-[#1A1A1A] cursor-pointer"
                >
                    <div className="flex items-start justify-between mb-8">
                        <div className="w-14 h-14 rounded-xl bg-[#1A1A1A] flex items-center justify-center text-white group-hover:bg-[#C08478] transition-colors">
                            <FileText size={26} strokeWidth={1.5} />
                        </div>
                        <ArrowRight
                            size={20}
                            className="text-gray-300 group-hover:text-[#C08478] group-hover:translate-x-1 transition-all"
                        />
                    </div>
                    <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3 tracking-tight">
                        Create an Invoice
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6">
                        Itemized billing with products, quantities, VAT and a full printable document.
                        Stock automatically decremented from your inventory.
                    </p>
                    <ul className="space-y-2 text-xs text-gray-500">
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#C08478]" />
                            Auto-numbered <span className="font-mono">UCYYMM######</span>
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#C08478]" />
                            Discount, delivery charge &amp; 20% VAT
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#C08478]" />
                            Split payments &amp; cheque tracking
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#C08478]" />
                            Save for Later (Draft) supported
                        </li>
                    </ul>
                </button>

                {/* Create Quotation Card */}
                <button
                    data-testid="choice-create-quotation"
                    onClick={() => navigate('/sales/quotation')}
                    className="choice-card group text-left bg-white border-2 border-[#E5E5E5] rounded-2xl p-8 lg:p-10 hover:border-[#8B5CF6] cursor-pointer"
                >
                    <div className="flex items-start justify-between mb-8">
                        <div className="w-14 h-14 rounded-xl bg-[#8B5CF6] flex items-center justify-center text-white group-hover:bg-[#1A1A1A] transition-colors">
                            <FileSignature size={26} strokeWidth={1.5} />
                        </div>
                        <ArrowRight
                            size={20}
                            className="text-gray-300 group-hover:text-[#8B5CF6] group-hover:translate-x-1 transition-all"
                        />
                    </div>
                    <h2 className="text-2xl font-bold text-[#1A1A1A] mb-3 tracking-tight">
                        Create a Quotation
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-6">
                        Send a formal offer to a customer. Stock is <strong>not</strong> deducted and no VAT is collected
                        until the quote is finalized into an invoice.
                    </p>
                    <ul className="space-y-2 text-xs text-gray-500">
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#8B5CF6]" />
                            Auto-numbered <span className="font-mono">DEVYYMM######</span>
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#8B5CF6]" />
                            Includes legal disclaimer (non-acquittée TVA)
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#8B5CF6]" />
                            Convert to a final invoice in one click
                        </li>
                        <li className="flex items-center gap-2">
                            <span className="w-1 h-1 rounded-full bg-[#8B5CF6]" />
                            Stays in Drafts &amp; Quotations until finalized
                        </li>
                    </ul>
                </button>
            </div>

            {/* Helper hint */}
            <p className="text-xs text-gray-400 max-w-3xl">
                Both documents reset their numbering counter every month. Drafts and quotations live in the{' '}
                <strong>Drafts &amp; Quotations</strong> tab until they're finalized.
            </p>
        </div>
    );
};

export default Sales;
